import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  toastSuccess,
  toastError,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import service from '@Service';
import type { OperationActionTriggerProps } from '@Constants/operations';
import { eventReportKeys } from '@Hooks/useEventReports';
import { useEventReportRunStore } from './eventReportRunStore';

// Manual runs sweep a wider window than the daily cron so an operator can catch
// recently-ended events that don't have a report yet.
const MANUAL_LOOKBACK_DAYS = 7;

export default function RunEventReportTrigger({
  soulId,
  onSuccess,
}: OperationActionTriggerProps) {
  const [running, setRunning] = useState(false);
  const queryClient = useQueryClient();

  const handleRun = async () => {
    setRunning(true);
    // Show the "thinking" thought while the (synchronous) job runs…
    useEventReportRunStore.getState().begin(soulId);
    try {
      const res = await service.runSummarizeEvents({
        lookbackDays: MANUAL_LOOKBACK_DAYS,
      });
      const generated = res?.data?.reportsGenerated ?? 0;
      queryClient.invalidateQueries({ queryKey: eventReportKeys.forSoul(soulId) });
      toastSuccess(
        generated > 0
          ? `Generated ${generated} event recap${generated === 1 ? '' : 's'}.`
          : 'No new events to recap.',
      );
      onSuccess();
    } catch {
      toastError('Failed to run event recaps.');
    } finally {
      // …and stop it deterministically — not by waiting for a new report to
      // appear, which never happens when everything is already reported.
      useEventReportRunStore.getState().end(soulId);
      setRunning(false);
    }
  };

  return (
    <Button variant="outline" size="sm" onClick={handleRun} loading={running}>
      {running ? 'Starting…' : 'Run Event Recap'}
    </Button>
  );
}
